import { FunctionComponent, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import {
  CategoryChips,
  FiltersButton,
  LocationChip,
  MobileFiltersPanel,
  PlanChip,
  PROVIDER_NAME_SEARCH_ID,
  ProviderNameSearchBar,
  SpecialistFilters
} from '@/modules/accredited-establishments/components/Filters'
import classNames from 'classnames'
import { PageTitle } from '@/modules/accredited-establishments/components/PageTitle'
import { useFilters } from '@/modules/accredited-establishments/hooks'
import { useDrawer } from '@/global/hooks'
import { zIndex } from '@/global/constants/z-index'
import { ALL_ID } from '@/modules/accredited-establishments/constants/filters'

/* Only the mobile panels reach it, and it carries the address query. */
const AddressSearchModal = dynamic(
  () =>
    import(
      '@/modules/accredited-establishments/components/Filters/AdressSearchModal/AddressSearchModal'
    ).then(mod => mod.AddressSearchModal),
  { ssr: false }
)

const Filters: FunctionComponent = () => {
  const { categories, isCollapsed, isListView, plan, specialty, subSpecialty } =
    useFilters()

  /*
   * Plan `ALL` is the empty state, so it does not count. The specialist chain
   * is guarded by the category for the same reason `MobileFiltersPanel` does
   * it: the panel only offers those sections under `SPECIALIST`.
   */
  const specialistChainCount = categories.includes('SPECIALIST')
    ? [specialty, subSpecialty].filter(Boolean).length
    : 0

  const mobileFiltersCount =
    (plan && plan.value !== ALL_ID ? 1 : 0) + specialistChainCount

  /* Mobile search by name: the bar replaces the title and the chip row. */
  const { isOpen: isSearchOpen } = useDrawer(PROVIDER_NAME_SEARCH_ID)

  const scrollerRef = useRef<HTMLDivElement>(null)

  const [isRowScrolled, setIsRowScrolled] = useState(false)

  const onRowScroll = () =>
    setIsRowScrolled((scrollerRef.current?.scrollLeft ?? 0) > 0)

  const chips = (
    <div
      className={classNames(
        'flex items-center justify-between gap-gl-spacing-04',
        'w-full py-gl-spacing-04'
      )}
    >
      {/*
        No mobile a fileira corre de ponta a ponta: as margens negativas
        desfazem o padding da coluna e o padding de volta so afasta o primeiro
        e o ultimo chip da borda. Sem isso a area de scroll termina antes da
        tela e o chip da ponta aparece cortado no meio.
      */}
      <div
        ref={scrollerRef}
        onScroll={onRowScroll}
        className='flex items-center gap-gl-spacing-01 overflow-x-auto scroll-invisible -mx-gl-spacing-03 px-gl-spacing-03 lg:mx-0 lg:px-0'
      >
        <div className='lg:hidden contents'>
          <FiltersButton
            collapsed={isRowScrolled}
            count={mobileFiltersCount}
          />
        </div>

        <CategoryChips />

        {/* On mobile the specialist chain lives in the filters panel. */}
        <div className='hidden lg:contents'>
          <SpecialistFilters />
        </div>
      </div>

      <div className='hidden lg:flex items-center gap-gl-spacing-03'>
        <PlanChip />
        <LocationChip />
      </div>
    </div>
  )

  /*
   * In list view the whole page scrolls and only the chip row sticks. `sticky`
   * has to be a direct child of the column that wraps the list.
   */
  if (isListView) {
    return (
      <>
        {!isSearchOpen ? <PageTitle /> : null}

        <div
          className={classNames(
            'sticky top-0 bg-gl-color-shades-white',
            // The white band has to outgrow the column padding.
            '-mx-gl-spacing-03 px-gl-spacing-03 lg:-mx-16 lg:px-16',
            zIndex.accreditedEstablishments.filters
          )}
        >
          {isSearchOpen ? <ProviderNameSearchBar /> : chips}
        </div>

        <MobileFiltersPanel />

        <AddressSearchModal />
      </>
    )
  }

  /* In map view the bar stays in flow and the map takes what is left. */
  return (
    <div
      className={classNames(
        'shrink-0 w-full bg-gl-color-shades-white',
        zIndex.accreditedEstablishments.filters
      )}
    >
      {/* `Ampliar mapa` hands the title area over to the map. */}
      {!isCollapsed ? <PageTitle /> : null}

      {chips}

      <MobileFiltersPanel />

      <AddressSearchModal />
    </div>
  )
}

export { Filters }
